import React, { useEffect, useState } from "react";
import { Modal } from "../../../../../../components/core/Modal/Modal";
import {
  CANCEL_LABEL,
  CONFIRM_LABEL,
  MODAL_CHANGE_STEPS_LABEL,
} from "../../../../../../constants/Dictionary";
import {
  Button,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import { FontWeightEnum } from "../../../../../../types/AppTypes";
import { RadioButton } from "../../../../../../components/core/RadioButton/RadioButton";
import { ReactComponent as CheckIcon } from "../../../../../../icons/check.svg";
import { ReactComponent as CancelIcon } from "../../../../../../icons/close.svg";
import { StyledChangeStatusModal } from "./ChangeStatusModal.styled";
import {
  cmSheetStatusLabels,
  CmSheetStateEnum,
  CmSheetEventEnum,
} from "../../../../../../constants/AppConstants";
import { theme } from "../../../../../../constants/StyledComponents";
import {
  CmSheetStateMachine,
  CollaboratorCmSheet,
} from "../../../../../../types/api/collaborators.type";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import { roleHasPermission } from "../../../../../../utils/roleAuth.utils";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import { currentStateIsMoreEqualThanTargetState } from "../../../../../../utils/cmSheet.utils";
import { useUpdateCmSheetStateMachine } from "../../../../../../hooks/cmSheet/stateMachine.hook";

interface ChangeStepsModalProps {
  isOpen: boolean;
  cmSheetStateMachine: CmSheetStateMachine;
  cmSheetInfo: CollaboratorCmSheet;
  closeModal: () => void;
  isInterim: boolean;
}

export const ChangeStatusModal = ({
  isOpen,
  cmSheetStateMachine,
  cmSheetInfo,
  closeModal,
  isInterim,
}: ChangeStepsModalProps) => {
  const { profile } = useAppSelector((state) => state.user);
  const [tmpCmSheetState, setTmpCmSheetState] = useState<CmSheetStateEnum>(
    cmSheetStateMachine.currentState,
  );
  const { updateCmSheetStateMachine, isUpdating } =
    useUpdateCmSheetStateMachine(cmSheetInfo.id);

  const draftIsEnabled = () => {
    if (cmSheetStateMachine.currentState === CmSheetStateEnum.DRAFT) {
      return true;
    }

    if (
      cmSheetStateMachine.possibleEvents.includes(
        CmSheetEventEnum.BACKTO_DRAFT,
      ) &&
      roleHasPermission(profile, CmSheetEventEnum.BACKTO_DRAFT)
    ) {
      return true;
    }

    return false;
  };

  const isRevisionRequestSent = () => {
    if (isInterim) {
      return true;
    }

    return cmSheetStateMachine.isRevisionRequestSent;
  };

  const advancementStatusIsEnabled = () => {
    if (
      cmSheetStateMachine.currentState ===
      CmSheetStateEnum.INTERMEDIATE_EVALUATION
    ) {
      return true;
    }

    if (
      cmSheetStateMachine.possibleEvents.includes(
        CmSheetEventEnum.BACKTO_VALUTAZIONE_INTERMEDIA,
      ) &&
      roleHasPermission(profile, CmSheetEventEnum.BACKTO_VALUTAZIONE_INTERMEDIA)
    ) {
      return true;
    }

    if (
      cmSheetStateMachine.possibleEvents.includes(CmSheetEventEnum.VALUTA) &&
      roleHasPermission(profile, CmSheetEventEnum.VALUTA) &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.internalGrade &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.softSkills &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.goals &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.careerPath &&
      isRevisionRequestSent()
    ) {
      return true;
    }

    return false;
  };

  const evaluationStatusIsEnabled = () => {
    if (
      cmSheetStateMachine.currentState === CmSheetStateEnum.FINAL_CALIBRATION
    ) {
      return true;
    }

    if (
      cmSheetStateMachine.possibleEvents.includes(
        CmSheetEventEnum.BACKTO_CALIBRATION_FINALE,
      ) &&
      roleHasPermission(profile, CmSheetEventEnum.BACKTO_CALIBRATION_FINALE)
    ) {
      return true;
    }

    if (
      cmSheetStateMachine.possibleEvents.includes(
        CmSheetEventEnum.CONCLUDI_SCHEDA,
      ) &&
      roleHasPermission(profile, CmSheetEventEnum.CONCLUDI_SCHEDA) &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.internalGrade &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.softSkills &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.goals &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.careerPath &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection
        .collaboratorFeedback &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.finalInterview &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.development &&
      isRevisionRequestSent()
    ) {
      return true;
    }

    return false;
  };

  const finalConsiderationsIsEnabled = () => {
    if (cmSheetStateMachine.currentState === CmSheetStateEnum.FINAL_FEEDBACK) {
      return true;
    }

    if (
      cmSheetStateMachine.possibleEvents.includes(
        CmSheetEventEnum.BACKTO_FEEDBACK_FINALE,
      ) &&
      roleHasPermission(profile, CmSheetEventEnum.BACKTO_FEEDBACK_FINALE)
    ) {
      return true;
    }

    if (
      cmSheetStateMachine.possibleEvents.includes(
        CmSheetEventEnum.INVIA_FEEDBACK,
      ) &&
      roleHasPermission(profile, CmSheetEventEnum.INVIA_FEEDBACK) &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.internalGrade &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.softSkills &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.goals &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.careerPath &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection
        .collaboratorFeedback &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.finalInterview &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.development
    ) {
      return true;
    }

    return false;
  };

  const archivedSheetIsEnabled = () => {
    if (cmSheetStateMachine.currentState === CmSheetStateEnum.ARCHIVED_SHEET) {
      return true;
    }

    if (
      cmSheetStateMachine.possibleEvents.includes(CmSheetEventEnum.ARCHIVIA) &&
      roleHasPermission(profile, CmSheetEventEnum.ARCHIVIA) &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.internalGrade &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.softSkills &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.goals &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.careerPath &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection
        .collaboratorFeedback &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.finalInterview &&
      cmSheetStateMachine.changeCMSheetStatusValidationSection.development &&
      isRevisionRequestSent()
    ) {
      return true;
    }

    return false;
  };

  const archivedSheetWithTerminationIsEnabled = () => {
    if (
      cmSheetStateMachine.currentState ===
      CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION
    ) {
      return true;
    }

    if (
      roleHasPermission(profile, CmSheetEventEnum.TERMINA) &&
      cmSheetStateMachine.possibleEvents.includes(CmSheetEventEnum.TERMINA)
    ) {
      return true;
    }

    return false;
  };

  const stateList = [
    {
      state: CmSheetStateEnum.DRAFT,
      enabled: draftIsEnabled(),
    },
    {
      state: CmSheetStateEnum.INTERMEDIATE_EVALUATION,
      enabled: advancementStatusIsEnabled(),
    },
    {
      state: CmSheetStateEnum.FINAL_CALIBRATION,
      enabled: evaluationStatusIsEnabled(),
    },
    {
      state: CmSheetStateEnum.FINAL_FEEDBACK,
      enabled: finalConsiderationsIsEnabled(),
    },
    {
      state: CmSheetStateEnum.ARCHIVED_SHEET,
      enabled: archivedSheetIsEnabled(),
    },
    {
      state: CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION,
      enabled: archivedSheetWithTerminationIsEnabled(),
    },
  ];

  const onConfirm = async (newState: CmSheetStateEnum) => {
    let event = null;

    if (
      currentStateIsMoreEqualThanTargetState(
        cmSheetStateMachine.currentState,
        newState,
      )
    ) {
      if (newState === CmSheetStateEnum.DRAFT) {
        event = CmSheetEventEnum.BACKTO_DRAFT;
      } else if (newState === CmSheetStateEnum.INTERMEDIATE_EVALUATION) {
        event = CmSheetEventEnum.BACKTO_VALUTAZIONE_INTERMEDIA;
      } else if (newState === CmSheetStateEnum.FINAL_CALIBRATION) {
        event = CmSheetEventEnum.BACKTO_CALIBRATION_FINALE;
      } else if (newState === CmSheetStateEnum.FINAL_FEEDBACK) {
        event = CmSheetEventEnum.BACKTO_FEEDBACK_FINALE;
      }
    } else if (newState === CmSheetStateEnum.INTERMEDIATE_EVALUATION) {
      if (isInterim) {
        await updateCmSheetStateMachine({
          event: CmSheetEventEnum.RICHIEDI_PRESA_VISIONE,
        });
      }
      event = CmSheetEventEnum.VALUTA;
    } else if (newState === CmSheetStateEnum.FINAL_CALIBRATION) {
      event = CmSheetEventEnum.CONCLUDI_SCHEDA;
    } else if (newState === CmSheetStateEnum.FINAL_FEEDBACK) {
      event = CmSheetEventEnum.INVIA_FEEDBACK;
    } else if (newState === CmSheetStateEnum.ARCHIVED_SHEET) {
      if (isInterim) {
        await updateCmSheetStateMachine({
          event: CmSheetEventEnum.RICHIEDI_PRESA_VISIONE,
        });
      }
      event = CmSheetEventEnum.ARCHIVIA;
    } else if (newState === CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION) {
      event = CmSheetEventEnum.TERMINA;
    }

    if (event) {
      await updateCmSheetStateMachine({ event });
      closeModal();
    }
  };

  useEffect(() => {
    setTmpCmSheetState(cmSheetStateMachine.currentState);
  }, [isOpen]);

  return (
    <Modal
      label={MODAL_CHANGE_STEPS_LABEL}
      fullSize={false}
      isOpen={isOpen}
      rightButton={
        <Button
          icon={<CheckIcon />}
          label={CONFIRM_LABEL}
          onClick={() => onConfirm(tmpCmSheetState)}
          disabled={tmpCmSheetState === cmSheetStateMachine.currentState}
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={closeModal}
        />
      }
    >
      <Loader loading={isUpdating}>
        <StyledChangeStatusModal>
          {stateList.map((item, index) => {
            return (
              <div className="option-label" key={index + 1}>
                <RadioButton
                  value={tmpCmSheetState === item.state}
                  onChange={() => setTmpCmSheetState(item.state)}
                  disabled={!item.enabled}
                />
                <BodyTyp
                  fontWeight={FontWeightEnum.MEDIUM}
                  variant="2"
                  {...(!item.enabled && { color: theme.colors.grey_2 })}
                >
                  {cmSheetStatusLabels[item.state]}
                </BodyTyp>
              </div>
            );
          })}
        </StyledChangeStatusModal>
      </Loader>
    </Modal>
  );
};
